/**
 * The shape vocabulary.
 *
 * Closed on purpose. A spec cannot draw an arbitrary polygon, it can only ask
 * for one of these, and each one is hand-written to look right in this style.
 * Building this library is the work; the data format is the easy half.
 *
 * Every shape here earned its place by recurring across the hand-written
 * generators. Nothing is speculative.
 */

import { GLYPHS } from "./glyphs.js";
import { resolveRamp, type Kit } from "./kit.js";
import { Canvas, mix, type Rgba } from "./raster.js";
import { type Frame, type Part, resolveBox, resolveCoord, resolveValue, role, type ShapeFields, type SpriteSpec } from "./spec.js";

export interface DrawContext {
	canvas: Canvas;
	kit: Kit;
	spec: SpriteSpec;
	frame: Frame;
}

export interface Shape {
	fields: ShapeFields;
	draw(ctx: DrawContext, part: Part): void;
}

const num = (p: Part, k: string) => p[k] as number;
const str = (p: Part, k: string) => p[k] as string;
const bool = (p: Part, k: string) => p[k] as boolean;
const box = (canvas: Canvas, p: Part, k = "inset") => resolveBox(p[k] as number[], canvas.width, canvas.height);
const point = (canvas: Canvas, p: Part, k = "at"): [number, number] => {
	const [x, y] = p[k] as [number, number];
	return [resolveCoord(x, canvas.width), resolveCoord(y, canvas.height)];
};
const value = (p: Part, k: string, frame: Frame) => resolveValue(p[k] as number | string, frame);

// ---- the isometric projection -------------------------------------------------
//
// One place, because every isometric shape has to agree about where a tile is or
// they will not stack. A tile is twice as wide as it is tall; that ratio is the
// projection and is not a setting.

type Corner = [number, number];

/**
 * A settled number between 0 and 1 for a seed and an index.
 *
 * Bitwright renders the same bytes every time it is asked, so nothing here may
 * reach for a random. This is the arithmetic that stands in for one.
 */
const settled = (seed: number, index: number): number => {
	const n = Math.sin(seed * 127.1 + index * 311.7) * 43758.5453;
	return n - Math.floor(n);
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const midpoint = (a: Corner, b: Corner): Corner => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

/**
 * The four corners of a footprint's ground diamond, north first and then
 * clockwise, centred on the shape's `at`.
 *
 * A w by h footprint is a rhombus rather than a square diamond, which matters as
 * soon as a building is longer one way than the other, so the corners come from
 * projecting the grid rather than from insetting a box.
 */
const isoCorners = (canvas: Canvas, p: Part): [Corner, Corner, Corner, Corner] => {
	const [ax, ay] = point(canvas, p);
	const tile = Math.max(2, num(p, "tile"));
	const [tw, th] = (p.tiles as [number, number] | undefined) ?? [1, 1];
	const w = Math.max(1, tw);
	const h = Math.max(1, th);
	const halfW = tile / 2;
	const halfH = tile / 4;

	// Grid (0,0), (w,0), (w,h), (0,h) projected, then shifted so the middle of the
	// bounding box lands on `at`.
	const cx = ((w - h) * halfW) / 2;
	const cy = ((w + h) * halfH) / 2;
	const at = (gx: number, gy: number): Corner => [
		ax + (gx - gy) * halfW - cx,
		ay + (gx + gy) * halfH - cy,
	];

	return [at(0, 0), at(w, 0), at(w, h), at(0, h)];
};

/**
 * A filled convex quad, by scanline.
 *
 * Every isometric face is a quad, so there is one filler rather than a diamond
 * routine and three wall routines that round their edges differently and leave
 * seams where they meet.
 */
const fillQuad = (canvas: Canvas, quad: Corner[], colour: Rgba): void => {
	const ys = quad.map((c) => c[1]);
	const top = Math.floor(Math.min(...ys));
	// The last vertex ends the shape rather than starting another row of it. A 32
	// wide tile is 16 rows tall, and painting the bottom point gives 17.
	const bottom = Math.ceil(Math.max(...ys)) - 1;

	for (let y = top; y <= bottom; y++) {
		let lo = Infinity;
		let hi = -Infinity;
		const see = (x: number) => {
			lo = Math.min(lo, x);
			hi = Math.max(hi, x);
		};

		// The whole band the row covers, not one line through the middle of it.
		// Sampling a single line clips a diamond a pixel short on each side,
		// because its widest point is a corner the line passes just below.
		for (let i = 0; i < quad.length; i++) {
			const a = quad[i] as Corner;
			const b = quad[(i + 1) % quad.length] as Corner;
			if (a[1] >= y && a[1] < y + 1) see(a[0]);
			if (a[1] === b[1]) continue;
			const lower = Math.min(a[1], b[1]);
			const upper = Math.max(a[1], b[1]);
			for (const at of [y, y + 1]) {
				if (at < lower || at > upper) continue;
				see(a[0] + ((at - a[1]) / (b[1] - a[1])) * (b[0] - a[0]));
			}
		}

		if (lo > hi) continue;
		const x0 = Math.round(lo);
		const x1 = Math.round(hi);
		canvas.rect(x0, y, Math.max(1, x1 - x0), 1, colour);
	}
};

/** The same quad as a keyline, for a tile that needs its edges to read. */
const outlineQuad = (canvas: Canvas, quad: Corner[], colour: Rgba): void => {
	for (let i = 0; i < quad.length; i++) {
		const a = quad[i] as Corner;
		const b = quad[(i + 1) % quad.length] as Corner;
		canvas.line(Math.round(a[0]), Math.round(a[1]), Math.round(b[0]), Math.round(b[1]), colour);
	}
};

// ---- shapes -------------------------------------------------------------------

export const SHAPES: Record<string, Shape> = {
	/**
	 * The bevelled box almost every machine is built on: flat body, a lit edge
	 * one side and a dark edge the other, an optional lip across the top and an
	 * accent line under it.
	 */
	casing: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", default: "casing" },
			lit: { type: "role", default: "casing_lit" },
			shadow: { type: "role", default: "casing_shadow" },
			bevel: { type: "enum", values: ["left", "right", "none"], default: "left" },
			bevelWidth: { type: "int", default: 2 },
			lipHeight: { type: "int", default: 0 },
			lipRole: { type: "role", default: "rim" },
			accentRole: { type: "role", default: "none" },
		},
		draw({ canvas, kit }, p) {
			const b = resolveBox(p.inset as number[], canvas.width, canvas.height);
			const bw = num(p, "bevelWidth");
			canvas.rect(b.x0, b.y0, b.w, b.h, role(kit, p.role));
			if (p.bevel !== "none" && bw > 0) {
				const litSide = p.bevel === "left" ? b.x0 : b.x1 - bw + 1;
				const darkSide = p.bevel === "left" ? b.x1 - bw + 1 : b.x0;
				canvas.rect(litSide, b.y0, bw, b.h, role(kit, p.lit));
				canvas.rect(darkSide, b.y0, bw, b.h, role(kit, p.shadow));
			}
			const lip = num(p, "lipHeight");
			if (lip > 0) {
				canvas.rect(b.x0, b.y0, b.w, lip, role(kit, p.lipRole));
				if (p.accentRole !== "none") canvas.rect(b.x0, b.y0 + lip, b.w, 1, role(kit, p.accentRole));
			}
		},
	},

	/**
	 * A sprite drawn dot by dot, in roles.
	 *
	 * Every other shape composes a thing out of parts that earned their place by
	 * recurring in real art. That is right for machines, which are boxes with
	 * fittings, and it is wrong for a cat, because a cat has to be drawn.
	 *
	 * The escape hatch does not escape the discipline. The pixels are placed by
	 * hand, but a cell names a role rather than a colour, so one drawing recolours
	 * to every coat in the kit without being touched. Hand-drawn silhouette,
	 * parametric colour, which is the pair nothing else here can give you.
	 *
	 * A character with no legend entry draws nothing, so "." is empty by
	 * convention and any unused character works the same way.
	 */
	art: {
		fields: {
			at: { type: "point", required: true },
			// Declared before `rows`, and it matters. The field list is what the
			// model is shown, and it writes a part in roughly the order it is given
			// them. A legend arriving after the rows is still a legal spec, but the
			// page cannot paint a row until it knows what the characters are made
			// of, so a drawing written that way sits blank until the last brace and
			// the wait goes back to being a wait.
			legend: { type: "legend", required: true },
			rows: { type: "rows", required: true },
		},
		draw({ canvas, kit }, p) {
			const [x0, y0] = point(canvas, p);
			const rows = p.rows as string[];
			const legend = p.legend as Record<string, string>;
			// Resolved once rather than per pixel: a 32x32 drawing is a thousand
			// cells and they are nearly all the same handful of roles.
			const colours = new Map(Object.entries(legend).map(([k, r]) => [k, role(kit, r)]));
			for (let dy = 0; dy < rows.length; dy++) {
				const row = [...rows[dy]];
				for (let dx = 0; dx < row.length; dx++) {
					const c = colours.get(row[dx]);
					if (c) canvas.set(x0 + dx, y0 + dy, c);
				}
			}
		},
	},

	/** A plain filled rectangle, optionally with a one pixel border. */
	panel: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", required: true },
			borderRole: { type: "role", default: "none" },
		},
		draw({ canvas, kit }, p) {
			const b = resolveBox(p.inset as number[], canvas.width, canvas.height);
			canvas.rect(b.x0, b.y0, b.w, b.h, role(kit, p.role));
			if (p.borderRole !== "none") canvas.frame(b.x0, b.y0, b.x1, b.y1, role(kit, p.borderRole));
		},
	},

	/**
	 * A one pixel frame and nothing else. Separate from panel because a trim
	 * ring rarely lines up exactly with the fill it surrounds, and forcing it
	 * to would make the format lie about the art.
	 */
	border: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", required: true },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			canvas.frame(b.x0, b.y0, b.x1, b.y1, role(kit, p.role));
		},
	},

	/** A dark inset window, the thing a meter or a readout sits in. */
	recess: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", default: "recess" },
			borderRole: { type: "role", default: "trim" },
		},
		draw({ canvas, kit }, p) {
			const b = resolveBox(p.inset as number[], canvas.width, canvas.height);
			canvas.rect(b.x0, b.y0, b.w, b.h, role(kit, p.role));
			if (p.borderRole !== "none") canvas.frame(b.x0, b.y0, b.x1, b.y1, role(kit, p.borderRole));
		},
	},

	/**
	 * A segmented bar that fills as its value rises, each segment taking the
	 * next colour off a ramp. This is the charge readout, the tank level and
	 * the progress bar, which were three separate hand-written versions.
	 */
	meter: {
		fields: {
			inset: { type: "box", required: true },
			value: { type: "value", required: true },
			segments: { type: "int", default: 6 },
			ramp: { type: "ramp", default: "charge" },
			offRole: { type: "role", default: "recess" },
			dividerRole: { type: "role", default: "casing_shadow" },
			direction: { type: "enum", values: ["up", "right"], default: "up" },
			litEdge: { type: "bool", default: true },
		},
		draw({ canvas, kit, frame }, p) {
			const b = resolveBox(p.inset as number[], canvas.width, canvas.height);
			const segments = num(p, "segments");
			const colours = resolveRamp(kit, str(p, "ramp"));
			const filled = Math.round(value(p, "value", frame) * segments);
			const off = role(kit, p.offRole);
			const divider = role(kit, p.dividerRole);
			const vertical = p.direction === "up";
			const span = vertical ? b.h : b.w;
			const step = span / segments;

			for (let i = 0; i < segments; i++) {
				const lit = i < filled;
				const colour = lit ? colours[Math.min(i, colours.length - 1)] : off;
				// Rounded from both ends so segments tile exactly, whatever the
				// height. Flooring each one independently leaves gaps.
				const far = Math.round(span - i * step);
				const near = Math.round(span - (i + 1) * step) + 1;
				if (vertical) {
					const y1 = b.y0 + far - 1;
					const y0 = b.y0 + near - 1;
					canvas.rect(b.x0, y0, b.w, y1 - y0 + 1, colour);
					if (lit && bool(p, "litEdge") && i === filled - 1) {
						canvas.rect(b.x0, y0, b.w, 1, role(kit, "white"));
					}
					canvas.rect(b.x0, y0 - 1, b.w, 1, divider);
				} else {
					const x0 = b.x0 + (span - far);
					const x1 = b.x0 + (span - near);
					canvas.rect(x0, b.y0, x1 - x0 + 1, b.h, colour);
					if (lit && bool(p, "litEdge") && i === filled - 1) {
						canvas.rect(x1, b.y0, 1, b.h, role(kit, "white"));
					}
					canvas.rect(x1 + 1, b.y0, 1, b.h, divider);
				}
			}
		},
	},

	/**
	 * A continuous liquid level rather than a segmented meter: bright surface
	 * line, body, a darker band at the base so it reads as deep, and a sheen
	 * down one side. Tanks, vats and troughs all draw this, separately.
	 */
	level: {
		fields: {
			inset: { type: "box", required: true },
			value: { type: "value", required: true },
			role: { type: "role", default: "liquid" },
			surfaceRole: { type: "role", default: "liquid_lit" },
			depthRole: { type: "role", default: "liquid_dark" },
			emptyRole: { type: "role", default: "chamber" },
			emptyLitRole: { type: "role", default: "chamber_lit" },
			sheen: { type: "enum", values: ["left", "right", "none"], default: "left" },
			bubbles: { type: "bool", default: false },
		},
		draw({ canvas, kit, frame }, p) {
			const b = box(canvas, p);
			canvas.rect(b.x0, b.y0, b.w, b.h, role(kit, p.emptyRole));
			canvas.rect(b.x0, b.y0, b.w, 1, role(kit, p.emptyLitRole));

			const filled = Math.round(value(p, "value", frame) * b.h);
			if (filled <= 0) return;
			const top = b.y1 - filled + 1;
			const surface = role(kit, p.surfaceRole);
			canvas.rect(b.x0, top, b.w, filled, role(kit, p.role));
			canvas.rect(b.x0, top, b.w, 1, surface);
			canvas.rect(b.x0, b.y1 - 1, b.w, 2, role(kit, p.depthRole));
			if (p.sheen !== "none") {
				canvas.rect(p.sheen === "left" ? b.x0 : b.x1, top, 1, filled, surface);
			}
			// Two specks of movement. Only once there is enough depth for them
			// to be inside the liquid rather than sitting on the rim.
			if (bool(p, "bubbles") && filled > 4) {
				canvas.dot(b.x1 - 2, b.y1 - 3, surface);
				canvas.dot(b.x0 + 2, top + 2, surface);
			}
		},
	},

	/**
	 * A row or column of tick marks lit up to a value. The side gauge on a
	 * tank, and the same thing on anything else with a readout down its edge.
	 */
	ticks: {
		fields: {
			inset: { type: "box", required: true },
			count: { type: "int", default: 5 },
			value: { type: "value", required: true },
			size: { type: "point", default: [2, 1] },
			onRole: { type: "role", default: "trim_lit" },
			offRole: { type: "role", default: "casing_shadow" },
			direction: { type: "enum", values: ["up", "right"], default: "up" },
		},
		draw({ canvas, kit, frame }, p) {
			const b = box(canvas, p);
			const count = num(p, "count");
			const [w, h] = p.size as [number, number];
			const v = value(p, "value", frame);
			const span = (p.direction === "up" ? b.h : b.w) - 1;
			const gaps = Math.max(count - 1, 1);
			for (let i = 0; i < count; i++) {
				// Slack on the comparison because a tick at exactly the water
				// line should read as reached, and floating point says no.
				const lit = i / gaps <= v + 0.001;
				const colour = role(kit, lit ? p.onRole : p.offRole);
				const offset = Math.floor((i * span) / gaps);
				if (p.direction === "up") canvas.rect(b.x0, b.y1 - offset, w, h, colour);
				else canvas.rect(b.x0 + offset, b.y0, w, h, colour);
			}
		},
	},

	/**
	 * A status light that changes colour by threshold rather than by ramp, so
	 * it reads at a glance instead of needing to be measured.
	 */
	lamp: {
		fields: {
			at: { type: "point", required: true },
			size: { type: "point", default: [3, 2] },
			value: { type: "value", default: 1 },
			thresholds: { type: "point", default: [0.15, 0.5] },
			roles: { type: "roles", default: ["bad", "warn", "good"] },
			highlight: { type: "bool", default: true },
		},
		draw({ canvas, kit, frame }, p) {
			const [x, y] = point(canvas, p);
			const [w, h] = p.size as [number, number];
			const v = value(p, "value", frame);
			const [low, high] = p.thresholds as [number, number];
			const roles = p.roles as string[];
			const colour = role(kit, v > high ? roles[2] : v > low ? roles[1] : roles[0]);
			canvas.rect(x, y, w, h, colour);
			if (bool(p, "highlight")) canvas.dot(x + 1, y, role(kit, "white"));
		},
	},

	/** A row of small indicator lights, however many of them are lit. */
	leds: {
		fields: {
			at: { type: "point", required: true },
			count: { type: "int", required: true },
			step: { type: "int", default: 3 },
			size: { type: "point", default: [1, 1] },
			value: { type: "value", default: 1 },
			onRole: { type: "role", default: "good" },
			offRole: { type: "role", default: "recess" },
			direction: { type: "enum", values: ["right", "down"], default: "right" },
		},
		draw({ canvas, kit, frame }, p) {
			const [x, y] = point(canvas, p);
			const count = num(p, "count");
			const step = num(p, "step");
			const [w, h] = p.size as [number, number];
			const lit = Math.round(value(p, "value", frame) * count);
			for (let i = 0; i < count; i++) {
				const colour = role(kit, i < lit ? p.onRole : p.offRole);
				const dx = p.direction === "right" ? i * step : 0;
				const dy = p.direction === "down" ? i * step : 0;
				canvas.rect(x + dx, y + dy, w, h, colour);
			}
		},
	},

	/**
	 * A small mark whose position is driven by the frame: a droplet falling from
	 * a spout, a bubble rising through a tank, a needle sweeping a gauge.
	 *
	 * This is the one thing the vocabulary was missing that stopped a whole
	 * family of generators porting. Every animated item in the game moves
	 * something along a line and most of them wrap. `offset` staggers a second
	 * and third copy so a column of bubbles does not travel in lockstep.
	 */
	mover: {
		fields: {
			from: { type: "point", required: true },
			to: { type: "point", required: true },
			value: { type: "value", required: true },
			role: { type: "role", required: true },
			trailRole: { type: "role", default: "none" },
			size: { type: "point", default: [1, 1] },
			count: { type: "int", default: 1 },
			offset: { type: "number", default: 0 },
			wrap: { type: "bool", default: false },
		},
		draw({ canvas, kit, frame }, p) {
			const [fx, fy] = point(canvas, p, "from");
			const [tx, ty] = point(canvas, p, "to");
			const [w, h] = p.size as [number, number];
			const colour = role(kit, p.role);
			const trail = p.trailRole === "none" ? null : role(kit, p.trailRole);
			const base = value(p, "value", frame);
			const count = num(p, "count");
			const offset = num(p, "offset");

			for (let i = 0; i < count; i++) {
				let t = base + i * offset;
				// Wrapping is what makes a loop seamless: the last frame has to
				// hand over to the first without a jump.
				if (bool(p, "wrap")) t = ((t % 1) + 1) % 1;
				else if (t < 0 || t > 1) continue;
				const x = Math.round(fx + (tx - fx) * t);
				const y = Math.round(fy + (ty - fy) * t);
				canvas.rect(x, y, w, h, colour);
				if (trail) canvas.rect(x, y + h, w, h, trail);
			}
		},
	},

	/**
	 * A body that is wider at one end: a plant pot, a mug, a roof, a nose cone.
	 *
	 * The shape library started out drawing machines, and machines are boxes, so
	 * everything in it had square sides. Almost nothing outside a factory does.
	 * This is the general answer, and it is still whole pixels: every row is its
	 * own rectangle, so the sides step cleanly instead of smearing.
	 */
	/**
	 * A body that bulges: widest across its middle and narrower at both ends.
	 *
	 * The vocabulary had no convex silhouette at all. `taper` narrows in one
	 * direction and `cylinder` has parallel sides, so anything barrel shaped came
	 * out as a rounded box and no amount of describing it in the sentence helped:
	 * the model was picking the closest word available, and none of them meant
	 * "fatter in the middle".
	 *
	 * Named for the shape rather than for the object, because the same curve is a
	 * barrel, a vase, an urn, a jar, a bottle, a bell and a tree trunk.
	 *
	 * `bands` are drawn by this shape rather than laid over it, which is the whole
	 * point: a band drawn separately is a straight bar that overhangs the sides,
	 * and one drawn here stops exactly where the body does because the same loop
	 * knows how wide each row is.
	 */
	belly: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", required: true },
			litRole: { type: "role", default: "none" },
			shadeRole: { type: "role", default: "none" },
			/** How much narrower the ends are than the middle. 0 is a cylinder. */
			waist: { type: "number", default: 0.3 },
			/** Where the widest point sits along the body, 0 at the start, 1 at the end. */
			widestAt: { type: "number", default: 0.5 },
			along: { type: "enum", values: ["vertical", "horizontal"], default: "vertical" },
			/** Hoops, staves or ribs across the body. They follow the curve. */
			bands: { type: "int", default: 0 },
			bandRole: { type: "role", default: "none" },
			bandThick: { type: "int", default: 1 },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const fill = role(kit, p.role);
			const lit = p.litRole === "none" ? null : role(kit, p.litRole);
			const shade = p.shadeRole === "none" ? null : role(kit, p.shadeRole);
			const band = p.bandRole === "none" ? null : role(kit, p.bandRole);

			const waist = Math.max(0, Math.min(1, num(p, "waist")));
			const widestAt = Math.max(0, Math.min(1, num(p, "widestAt")));
			const vertical = p.along !== "horizontal";
			const along = vertical ? b.h : b.w;
			const across = vertical ? b.w : b.h;
			const bands = Math.max(0, num(p, "bands"));
			const thick = Math.max(1, num(p, "bandThick"));

			// Which rows a band covers, worked out once. Evenly spaced and never on
			// the very first or last row, where a hoop would read as the rim.
			const banded = new Set<number>();
			for (let k = 1; k <= bands; k++) {
				const centre = Math.round((along * k) / (bands + 1));
				for (let t = 0; t < thick; t++) banded.add(centre + t - Math.floor(thick / 2));
			}

			for (let i = 0; i < along; i++) {
				const at = along === 1 ? widestAt : i / (along - 1);
				// Distance from the widest point, normalised so both ends reach 1
				// whether or not the bulge sits in the middle.
				const reach = Math.max(widestAt, 1 - widestAt) || 1;
				const d = Math.min(1, Math.abs(at - widestAt) / reach);
				// Squared, so the curve is flat around the belly and falls away at the
				// ends. Linear here draws a diamond rather than a barrel.
				const span = Math.max(1, Math.round(across * (1 - waist * d * d)));
				const pad = Math.floor((across - span) / 2);
				const paint = banded.has(i) && band ? band : fill;

				if (vertical) {
					const y = b.y0 + i;
					canvas.rect(b.x0 + pad, y, span, 1, paint);
					if (lit) canvas.dot(b.x0 + pad, y, lit);
					if (shade) canvas.dot(b.x0 + pad + span - 1, y, shade);
				} else {
					const x = b.x0 + i;
					canvas.rect(x, b.y0 + pad, 1, span, paint);
					if (lit) canvas.dot(x, b.y0 + pad, lit);
					if (shade) canvas.dot(x, b.y0 + pad + span - 1, shade);
				}
			}
		},
	},

	taper: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", required: true },
			litRole: { type: "role", default: "none" },
			shadeRole: { type: "role", default: "none" },
			narrow: { type: "number", default: 0.4 },
			towards: { type: "enum", values: ["top", "bottom", "left", "right"], default: "top" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const fill = role(kit, p.role);
			const lit = p.litRole === "none" ? null : role(kit, p.litRole);
			const shade = p.shadeRole === "none" ? null : role(kit, p.shadeRole);
			const narrow = Math.max(0, Math.min(1, num(p, "narrow")));
			const vertical = p.towards === "top" || p.towards === "bottom";
			const along = vertical ? b.h : b.w;
			const across = vertical ? b.w : b.h;
			// Row 0 is the top, or the left. "towards top" means narrowest there,
			// so t has to be 1 at row 0 and 0 at the far end, not the other way
			// round. Getting this backwards turns a nose cone into a funnel.
			const growing = p.towards === "bottom" || p.towards === "right";

			for (let i = 0; i < along; i++) {
				// t runs 0 at the wide end to 1 at the narrow one.
				const at = along === 1 ? 0 : i / (along - 1);
				const t = growing ? at : 1 - at;
				const span = Math.max(1, Math.round(across * (1 - narrow * t)));
				const pad = Math.floor((across - span) / 2);
				if (vertical) {
					const y = b.y0 + i;
					canvas.rect(b.x0 + pad, y, span, 1, fill);
					if (lit) canvas.dot(b.x0 + pad, y, lit);
					if (shade) canvas.dot(b.x0 + pad + span - 1, y, shade);
				} else {
					const x = b.x0 + i;
					canvas.rect(x, b.y0 + pad, 1, span, fill);
					if (lit) canvas.dot(x, b.y0 + pad, lit);
					if (shade) canvas.dot(x, b.y0 + pad + span - 1, shade);
				}
			}
		},
	},

	/**
	 * An ellipse with nothing in it: a mug handle, a porthole surround, a hoop,
	 * the rim of anything seen at an angle.
	 *
	 * Separate from `disc` because filling and then covering the middle back up
	 * only works if you know what is behind it, and usually you do not.
	 */
	/**
	 * A cuboid seen at three quarters: a top face, a front face and one side.
	 *
	 * The vocabulary was entirely axis aligned, so anything boxy drawn at an
	 * angle had to be faked by stacking rectangles and putting a lighter strip
	 * along the top. That reads as a flat panel with a highlight, which is why
	 * beds, chests and consoles kept coming back looking wrong at this angle.
	 *
	 * The projection is oblique rather than true perspective, which is what the
	 * games this format is aimed at actually use: the depth is a fixed diagonal
	 * offset, so a row of these all recede at the same rate and tile together.
	 */
	block: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", required: true },
			topRole: { type: "role", required: true },
			sideRole: { type: "role", required: true },
			depth: { type: "int", default: 6 },
			edgeRole: { type: "role", default: "none" },
			facing: { type: "enum", values: ["left", "right"], default: "right" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			// The depth eats into both the width and the height, so it cannot be
			// larger than either or the front face disappears.
			const d = Math.max(1, Math.min(num(p, "depth"), b.w - 1, b.h - 1));
			const front = role(kit, p.role);
			const top = role(kit, p.topRole);
			const side = role(kit, p.sideRole);
			const edge = p.edgeRole === "none" ? null : role(kit, p.edgeRole);
			const right = p.facing !== "left";
			const fw = b.w - d;
			const fh = b.h - d;

			// The top face, drawn back to front. Each row forward steps one pixel
			// towards the viewer, which is the whole of the projection.
			for (let i = 0; i < d; i++) {
				const shift = d - i;
				canvas.rect(right ? b.x0 + shift : b.x0 + d - shift, b.y0 + i, fw, 1, top);
			}

			// The side, one slanted column per step of depth.
			for (let i = 0; i < d; i++) {
				const x = right ? b.x0 + fw + i : b.x0 + d - 1 - i;
				canvas.rect(x, b.y0 + d - 1 - i, 1, fh, side);
			}

			canvas.rect(right ? b.x0 : b.x0 + d, b.y0 + d, fw, fh, front);
			if (edge) canvas.rect(right ? b.x0 : b.x0 + d, b.y0 + d, fw, 1, edge);
		},
	},

	// ---- isometric ------------------------------------------------------------
	//
	// A town seen from the corner, at the 2:1 projection every isometric game
	// uses: a tile is twice as wide as it is tall. Before these, an isometric
	// building had to go through `art`, one character per pixel, which asks the
	// author to be a pixel artist rather than to describe a thing. A town builder
	// wants hundreds of buildings, and sixty rows of characters each is not a
	// vocabulary.
	//
	// All four share one anchor: `at` is the middle of the footprint's ground
	// diamond. Stack them at the same `at` and they line up, which is the whole
	// point: ground, then block, then roof, then whatever goes on the walls.

	iso_ground: {
		fields: {
			at: { type: "point", required: true },
			tile: { type: "int", default: 32 },
			tiles: { type: "point", default: [1, 1] },
			role: { type: "role", required: true },
			edgeRole: { type: "role", default: "none" },
		},
		draw({ canvas, kit }, p) {
			const corners = isoCorners(canvas, p);
			fillQuad(canvas, corners, role(kit, p.role));
			if (p.edgeRole !== "none") outlineQuad(canvas, corners, role(kit, p.edgeRole));
		},
	},

	/**
	 * The workhorse: a footprint pushed up into a solid. A lit wall one side, a
	 * dark wall the other and a flat top, which is a house, a crate, a plinth or
	 * a platform depending on what is stacked on it.
	 */
	iso_block: {
		fields: {
			at: { type: "point", required: true },
			tile: { type: "int", default: 32 },
			tiles: { type: "point", default: [1, 1] },
			height: { type: "int", default: 12 },
			role: { type: "role", required: true },
			leftRole: { type: "role", required: true },
			rightRole: { type: "role", required: true },
		},
		draw({ canvas, kit }, p) {
			const base = isoCorners(canvas, p);
			const h = Math.max(0, num(p, "height"));
			const top = base.map((c) => [c[0], c[1] - h] as Corner);

			// Walls first, then the top over them, so the roofline is clean rather
			// than fighting whichever wall was drawn last.
			fillQuad(canvas, [base[3], base[2], top[2], top[3]], role(kit, p.leftRole));
			fillQuad(canvas, [base[2], base[1], top[1], top[2]], role(kit, p.rightRole));
			fillQuad(canvas, top, role(kit, p.role));
		},
	},

	/**
	 * A pitched roof over a footprint. Two slopes meeting at a ridge, which is
	 * the difference between a building and a box with a lid.
	 */
	iso_roof: {
		fields: {
			at: { type: "point", required: true },
			tile: { type: "int", default: 32 },
			tiles: { type: "point", default: [1, 1] },
			rise: { type: "int", default: 8 },
			role: { type: "role", required: true },
			shadeRole: { type: "role", required: true },
			along: { type: "enum", values: ["x", "y"], default: "x" },
		},
		draw({ canvas, kit }, p) {
			const [n, e, s, w] = isoCorners(canvas, p);
			const rise = Math.max(0, num(p, "rise"));
			const up = (c: Corner): Corner => [c[0], c[1] - rise];

			// The ridge runs between the middles of two opposite eaves. Which two is
			// the only thing `along` decides, and it is what turns the roof to face
			// the other way.
			const [a, b, c, d] = p.along === "y" ? [e, s, w, n] : [n, e, s, w];
			const r1 = up(midpoint(a, b));
			const r2 = up(midpoint(c, d));

			// Each slope keeps a whole eave as its lower edge, so the two together
			// cover the footprint exactly. Taking the ridge between the wrong pair of
			// sides makes a slope double back across its own roof.
			fillQuad(canvas, [r2, d, a, r1], role(kit, p.shadeRole));
			fillQuad(canvas, [r1, b, c, r2], role(kit, p.role));
		},
	},

	/**
	 * A rectangle lying on one of the two walls a viewer can see: a door, a
	 * window, a sign, a hatch. Placed in the wall's own terms, so it stays put
	 * when the building it is on changes size.
	 */
	iso_face: {
		fields: {
			at: { type: "point", required: true },
			tile: { type: "int", default: 32 },
			tiles: { type: "point", default: [1, 1] },
			height: { type: "int", default: 12 },
			side: { type: "enum", values: ["left", "right"], default: "right" },
			along: { type: "number", default: 0.5 },
			up: { type: "int", default: 0 },
			width: { type: "int", default: 6 },
			tall: { type: "int", default: 8 },
			role: { type: "role", required: true },
		},
		draw({ canvas, kit }, p) {
			const [, e, s, w] = isoCorners(canvas, p);
			const h = Math.max(0, num(p, "height"));
			const wide = Math.max(1, num(p, "width"));
			const tall = Math.max(1, num(p, "tall"));
			const up = Math.max(0, num(p, "up"));

			// The wall's base runs between two ground corners. Everything else is a
			// fraction along that line, which is why a door stays centred on a wall
			// whatever the footprint is.
			const [from, to] = p.side === "left" ? [w, s] : [s, e];
			const span = Math.hypot(to[0] - from[0], to[1] - from[1]) || 1;
			const t = clamp01(num(p, "along"));
			const half = wide / (2 * span);
			const lo = Math.max(0, t - half);
			const hi = Math.min(1, t + half);

			const foot = (at: number): Corner => [
				from[0] + (to[0] - from[0]) * at,
				from[1] + (to[1] - from[1]) * at,
			];
			const lift = (c: Corner, by: number): Corner => [c[0], c[1] - by];

			// Kept on the wall rather than allowed to run off the top of it.
			const bottom = Math.min(up, Math.max(0, h - 1));
			const topOf = Math.min(bottom + tall, h);

			fillQuad(canvas, [
				lift(foot(lo), bottom),
				lift(foot(hi), bottom),
				lift(foot(hi), topOf),
				lift(foot(lo), topOf),
			], role(kit, p.role));
		},
	},

	/**
	 * Marks scattered inside a footprint: tufts on grass, gravel on a path, chips
	 * on stone. Settled by the seed rather than random, because a tile that redraws
	 * differently is not a tile, and clipped to the diamond, because a mark over the
	 * edge shows as a seam the moment the tile is repeated.
	 */
	iso_scatter: {
		fields: {
			at: { type: "point", required: true },
			tile: { type: "int", default: 32 },
			tiles: { type: "point", default: [1, 1] },
			role: { type: "role", required: true },
			density: { type: "int", default: 24 },
			size: { type: "int", default: 1 },
			seed: { type: "int", default: 1 },
		},
		draw({ canvas, kit }, p) {
			const [n, e, , w] = isoCorners(canvas, p);
			const colour = role(kit, p.role);
			const count = Math.max(0, num(p, "density"));
			const size = Math.max(1, num(p, "size"));
			const seed = num(p, "seed");

			// Picked in the diamond own coordinates and then projected, so the marks
			// spread evenly over the tile instead of bunching towards its middle the
			// way rejecting points from a box would.
			for (let i = 0; i < count; i++) {
				const u = settled(seed, i * 2 + 1);
				const v = settled(seed, i * 2 + 2);
				const x = n[0] + (e[0] - n[0]) * u + (w[0] - n[0]) * v;
				const y = n[1] + (e[1] - n[1]) * u + (w[1] - n[1]) * v;
				// Kept a mark in from the edge so nothing hangs over it.
				if (u + v > 1 - size / Math.max(e[0] - w[0], 1)) continue;
				canvas.rect(Math.round(x), Math.round(y), size, Math.max(1, Math.round(size / 2)), colour);
			}
		},
	},

	ring: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", required: true },
			thickness: { type: "int", default: 1 },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const colour = role(kit, p.role);
			const t = Math.max(1, num(p, "thickness"));
			const rx = b.w / 2;
			const ry = b.h / 2;
			const cx = b.x0 + rx - 0.5;
			const cy = b.y0 + ry - 0.5;
			const inside = (x: number, y: number, shrink: number) => {
				const dx = (x - cx) / Math.max(0.5, rx - shrink);
				const dy = (y - cy) / Math.max(0.5, ry - shrink);
				return dx * dx + dy * dy <= 1;
			};
			for (let y = b.y0; y <= b.y1; y++) {
				for (let x = b.x0; x <= b.x1; x++) {
					if (inside(x, y, 0) && !inside(x, y, t)) canvas.dot(x, y, colour);
				}
			}
		},
	},

	/**
	 * One of the marks in the glyph table, placed by its top-left corner.
	 *
	 * Its colour can be driven, which is how a medical cross pulses or a warning
	 * symbol goes from dim to hot. Same threshold semantics as `lamp`, so the
	 * two behave the same way for the same reason.
	 */
	glyph: {
		fields: {
			id: { type: "glyph", required: true },
			at: { type: "point", required: true },
			role: { type: "role", required: true },
			value: { type: "value", default: 1 },
			thresholds: { type: "point", default: [0.25, 0.75] },
			roles: { type: "roles", default: [] },
		},
		draw({ canvas, kit, frame }, p) {
			const art = GLYPHS[str(p, "id")];
			const [x, y] = point(canvas, p);
			const steps = p.roles as string[];
			let colour = role(kit, p.role);
			if (steps.length === 3) {
				const v = value(p, "value", frame);
				const [low, high] = p.thresholds as [number, number];
				colour = role(kit, v > high ? steps[2] : v > low ? steps[1] : steps[0]);
			}
			art.forEach((row, dy) => {
				[...row].forEach((ch, dx) => {
					if (ch === "#") canvas.dot(x + dx, y + dy, colour);
				});
			});
		},
	},

	/** Repeated blocks along an edge: brass contacts, bolts, feet. */
	studs: {
		fields: {
			inset: { type: "box", required: true },
			along: { type: "edge", default: "bottom" },
			step: { type: "int", default: 4 },
			size: { type: "point", default: [2, 2] },
			role: { type: "role", default: "brass" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const [w, h] = p.size as [number, number];
			const colour = role(kit, p.role);
			const step = num(p, "step");
			const along = str(p, "along");
			if (along === "top" || along === "bottom") {
				const y = along === "top" ? b.y0 : b.y1 - h + 1;
				for (let x = b.x0; x <= b.x1 - w + 1; x += step) canvas.rect(x, y, w, h, colour);
			} else {
				const x = along === "left" ? b.x0 : b.x1 - w + 1;
				for (let y = b.y0; y <= b.y1 - h + 1; y += step) canvas.rect(x, y, w, h, colour);
			}
		},
	},

	/** Terminal posts across the top, one pair per column. */
	posts: {
		fields: {
			inset: { type: "box", required: true },
			columns: { type: "int", default: 1 },
			y: { type: "int", default: 0 },
			size: { type: "point", default: [3, 3] },
			gap: { type: "int", default: 2 },
			role: { type: "role", default: "rim" },
			capRole: { type: "role", default: "rivet" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const columns = num(p, "columns");
			const [w, h] = p.size as [number, number];
			const gap = num(p, "gap");
			const y = resolveCoord(num(p, "y"), canvas.height);
			const colour = role(kit, p.role);
			const cap = role(kit, p.capRole);
			const colWidth = b.w / columns;
			for (let c = 0; c < columns; c++) {
				const cx = b.x0 + colWidth * (c + 0.5);
				// Floor, not round. With an even number of columns the centre
				// lands on a half pixel, and rounding puts the two posts of a
				// pair a pixel apart from each other.
				const left = Math.floor(cx - gap / 2 - w);
				const right = Math.floor(cx + gap / 2);
				canvas.rect(left, y, w, h, colour);
				canvas.rect(right, y, w, h, colour);
				// Cap sits on the middle of each post, which is what reads as a
				// terminal rather than a chamfer on the block.
				canvas.dot(left + (w >> 1), y, cap);
				canvas.dot(right + (w >> 1), y, cap);
			}
		},
	},

	/**
	 * Evenly spaced lines: vents, radiators, speaker fronts, and turned the
	 * other way, the plank joins on anything made of boards.
	 */
	grille: {
		fields: {
			inset: { type: "box", required: true },
			step: { type: "int", default: 2 },
			role: { type: "role", default: "casing_shadow" },
			gapRole: { type: "role", default: "none" },
			direction: { type: "enum", values: ["rows", "columns"], default: "rows" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const step = num(p, "step");
			const bar = role(kit, p.role);
			const gap = p.gapRole === "none" ? null : role(kit, p.gapRole);
			const rows = p.direction === "rows";
			const from = rows ? b.y0 : b.x0;
			const to = rows ? b.y1 : b.x1;
			for (let i = from; i <= to; i++) {
				const colour = (i - from) % step === 0 ? bar : gap;
				if (!colour) continue;
				if (rows) canvas.rect(b.x0, i, b.w, 1, colour);
				else canvas.rect(i, b.y0, 1, b.h, colour);
			}
		},
	},

	/**
	 * A filled ellipse on the pixel grid. Handles, dials, portholes, tanks seen
	 * end on. Eight of the hand-written generators reach for PIL's ellipse.
	 */
	/**
	 * An upright cylinder seen from slightly above: an elliptical top, a barrel
	 * body and a rounded foot.
	 *
	 * The other half of the angle problem that `block` solves for boxes. A great
	 * deal of what anybody asks for is a cylinder, a barrel, a can, a jar, a pot,
	 * a tank, a mug, and with only rectangles and full ellipses to hand the model
	 * builds them as a stack of bands with a disc balanced on top.
	 */
	cylinder: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", required: true },
			topRole: { type: "role", required: true },
			litRole: { type: "role", default: "none" },
			shadeRole: { type: "role", default: "none" },
			/** How deep the elliptical top is. Larger means seen from higher up. */
			rim: { type: "int", default: 6 },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const fill = role(kit, p.role);
			const top = role(kit, p.topRole);
			const lit = p.litRole === "none" ? null : role(kit, p.litRole);
			const shade = p.shadeRole === "none" ? null : role(kit, p.shadeRole);
			// The rim has to fit twice over: once at the top and once at the foot.
			const rim = Math.max(2, Math.min(num(p, "rim"), Math.floor((b.h - 1) / 2)));
			const rx = b.w / 2;
			const cx = b.x0 + rx - 0.5;

			// How far in the body is at a given row, following the foot's curve.
			const half = (cy: number, y: number, ry: number) => {
				const dy = (y - cy) / ry;
				const t = 1 - dy * dy;
				return t <= 0 ? -1 : rx * Math.sqrt(t) - 0.5;
			};

			const footCy = b.y1 - rim / 2 + 0.5;
			for (let y = b.y0 + Math.floor(rim / 2); y <= b.y1; y++) {
				// Straight sided until the foot starts curving away.
				const span = y <= b.y1 - rim / 2 ? rx - 0.5 : half(footCy, y, rim / 2);
				if (span < 0) continue;
				const x0 = Math.round(cx - span);
				const x1 = Math.round(cx + span);
				canvas.rect(x0, y, x1 - x0 + 1, 1, fill);
				if (lit) canvas.dot(x0, y, lit);
				if (shade) canvas.dot(x1, y, shade);
			}

			// The opening, drawn last so it sits on top of the body.
			const topCy = b.y0 + rim / 2 - 0.5;
			for (let y = b.y0; y <= b.y0 + rim; y++) {
				const span = half(topCy, y, rim / 2);
				if (span < 0) continue;
				const x0 = Math.round(cx - span);
				canvas.rect(x0, y, Math.round(cx + span) - x0 + 1, 1, top);
			}
		},
	},

	disc: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", required: true },
			borderRole: { type: "role", default: "none" },
			litRole: { type: "role", default: "none" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const fill = role(kit, p.role);
			const rx = b.w / 2;
			const ry = b.h / 2;
			const cx = b.x0 + rx - 0.5;
			const cy = b.y0 + ry - 0.5;
			const inside = (x: number, y: number) => {
				const dx = (x - cx) / rx;
				const dy = (y - cy) / ry;
				return dx * dx + dy * dy <= 1;
			};
			for (let y = b.y0; y <= b.y1; y++) {
				for (let x = b.x0; x <= b.x1; x++) if (inside(x, y)) canvas.dot(x, y, fill);
			}
			if (p.borderRole !== "none") {
				const edge = role(kit, p.borderRole);
				for (let y = b.y0; y <= b.y1; y++) {
					for (let x = b.x0; x <= b.x1; x++) {
						if (!inside(x, y)) continue;
						// An edge pixel is one with a neighbour outside the ellipse.
						if (!inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1)) {
							canvas.dot(x, y, edge);
						}
					}
				}
			}
			if (p.litRole !== "none") {
				// Catch-light up and left, the usual pixel-art light direction.
				canvas.dot(Math.round(cx - rx / 2), Math.round(cy - ry / 2), role(kit, p.litRole));
			}
		},
	},

	/** Diagonal warning stripes. */
	hazard: {
		fields: {
			inset: { type: "box", required: true },
			step: { type: "int", default: 3 },
			role: { type: "role", default: "hazard" },
			altRole: { type: "role", default: "casing_darkest" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const step = num(p, "step");
			const a = role(kit, p.role);
			const c = role(kit, p.altRole);
			for (let y = b.y0; y <= b.y1; y++) {
				for (let x = b.x0; x <= b.x1; x++) {
					canvas.dot(x, y, Math.floor((x + y) / step) % 2 === 0 ? a : c);
				}
			}
		},
	},

	/** A straight run of pipe with a lit top edge, horizontal or vertical. */
	pipe: {
		fields: {
			from: { type: "point", required: true },
			to: { type: "point", required: true },
			width: { type: "int", default: 3 },
			role: { type: "role", default: "rim" },
			litRole: { type: "role", default: "rivet" },
		},
		draw({ canvas, kit }, p) {
			const [fx, fy] = point(canvas, p, "from");
			const [tx, ty] = point(canvas, p, "to");
			const w = num(p, "width");
			const body = role(kit, p.role);
			const lit = role(kit, p.litRole);
			if (fy === ty) {
				const x0 = Math.min(fx, tx);
				canvas.rect(x0, fy, Math.abs(tx - fx) + 1, w, body);
				canvas.rect(x0, fy, Math.abs(tx - fx) + 1, 1, lit);
			} else {
				const y0 = Math.min(fy, ty);
				canvas.rect(fx, y0, w, Math.abs(ty - fy) + 1, body);
				canvas.rect(fx, y0, 1, Math.abs(ty - fy) + 1, lit);
			}
		},
	},

	/** Single pixel highlights along an edge. */
	rivets: {
		fields: {
			inset: { type: "box", required: true },
			along: { type: "edge", default: "top" },
			step: { type: "int", default: 4 },
			role: { type: "role", default: "rivet" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const colour = role(kit, p.role);
			const step = num(p, "step");
			const along = str(p, "along");
			if (along === "top" || along === "bottom") {
				const y = along === "top" ? b.y0 : b.y1;
				for (let x = b.x0; x <= b.x1; x += step) canvas.dot(x, y, colour);
			} else {
				const x = along === "left" ? b.x0 : b.x1;
				for (let y = b.y0; y <= b.y1; y += step) canvas.dot(x, y, colour);
			}
		},
	},

	/**
	 * A translucent sheen over whatever is underneath. The one shape that
	 * blends, because glass over a lit readout is the one case where replacing
	 * the pixel is wrong.
	 */
	glass: {
		fields: {
			inset: { type: "box", required: true },
			role: { type: "role", default: "glass" },
			glintRole: { type: "role", default: "none" },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			canvas.rect(b.x0, b.y0, b.w, b.h, role(kit, p.role));
			if (p.glintRole !== "none") {
				// A short diagonal catch-light in the top-left, the pixel-art
				// shorthand for "this is behind glass".
				const n = Math.max(2, Math.min(b.w, b.h) - 2);
				canvas.line(b.x0 + 1, b.y0 + n, b.x0 + n, b.y0 + 1, role(kit, p.glintRole));
			}
		},
	},

	/**
	 * A vertical gradient wash, for tanks and backdrops. Interpolates in whole
	 * steps between two roles so it stays banded rather than smooth.
	 */
	gradient: {
		fields: {
			inset: { type: "box", required: true },
			from: { type: "role", required: true },
			to: { type: "role", required: true },
			bands: { type: "int", default: 4 },
		},
		draw({ canvas, kit }, p) {
			const b = box(canvas, p);
			const bands = num(p, "bands");
			const a = role(kit, p.from) as Rgba;
			const c = role(kit, p.to) as Rgba;
			const step = b.h / bands;
			for (let i = 0; i < bands; i++) {
				const y0 = b.y0 + Math.round(i * step);
				const y1 = b.y0 + Math.round((i + 1) * step) - 1;
				canvas.rect(b.x0, y0, b.w, y1 - y0 + 1, mix(a, c, bands === 1 ? 0 : i / (bands - 1)));
			}
		},
	},
};

export const SHAPE_FIELDS: Record<string, ShapeFields> = Object.fromEntries(
	Object.entries(SHAPES).map(([name, shape]) => [name, shape.fields]),
);
